'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  Popover,
  PopoverButton,
  PopoverBackdrop,
  PopoverPanel,
} from '@headlessui/react'
import clsx from 'clsx'

import { Container } from '@/components/sections/container'
import { Button } from '@/components/sections/button'
import { Logo } from '@/components/sections/logo'
import {
  getInPageAnchorId,
  normalizeAnchor,
  normalizeSectionId,
} from '@/components/sections/section-utils'

function NavLink({ href, children, onAnchorClick }) {
  return (
    <Link
      href={href}
      className="inline-block rounded-lg px-2 py-1 text-sm text-primary hover:bg-light/20 hover:text-dark"
      onClick={onAnchorClick ? (event) => onAnchorClick(event, href) : undefined}
    >
      {children}
    </Link>
  )
}

function MobileNavLink({ href, children, onAnchorClick }) {
  return (
    <PopoverButton
      as={Link}
      href={href}
      className="block w-full p-2"
      onClick={onAnchorClick ? (event) => onAnchorClick(event, href) : undefined}
    >
      {children}
    </PopoverButton>
  )
}

function MobileNavIcon({ open }) {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5 overflow-visible stroke-primary"
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <path
        d="M0 1H14M0 7H14M0 13H14"
        className={clsx(
          'origin-center transition',
          open && 'scale-90 opacity-0',
        )}
      />
      <path
        d="M2 2L12 12M12 2L2 12"
        className={clsx(
          'origin-center transition',
          !open && 'scale-90 opacity-0',
        )}
      />
    </svg>
  )
}

function MobileNavigation({ links, onAnchorClick }) {
  return (
    <Popover>
      <PopoverButton
        className="relative z-10 flex h-8 w-8 items-center justify-center focus:not-data-focus:outline-hidden"
        aria-label="Toggle Navigation"
      >
        {({ open }) => <MobileNavIcon open={open} />}
      </PopoverButton>
      <PopoverBackdrop
        transition
        className="fixed inset-0 bg-light/35 duration-150 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in"
      />
      <PopoverPanel
        transition
        className="absolute inset-x-0 top-full mt-4 flex origin-top flex-col rounded-2xl bg-white p-4 text-lg tracking-tight text-dark shadow-xl ring-1 ring-dark/10 data-closed:scale-95 data-closed:opacity-0 data-enter:duration-150 data-enter:ease-out data-leave:duration-100 data-leave:ease-in"
      >
        <div className="-my-1 flex flex-col gap-2">
          {links.length
            ? links.map((link, index) => (
                <MobileNavLink
                  href={link.href}
                  key={`${link.label}-${index}`}
                  onAnchorClick={onAnchorClick}
                >
                  {link.label}
                </MobileNavLink>
              ))
            : null}
          <hr className="my-2 border-light/30" />
        </div>
      </PopoverPanel>
    </Popover>
  )
}

function normalizeLinks(links) {
  if (!Array.isArray(links)) {
    return []
  }

  return links
    .map((link) => ({
      label: String(link?.label || '').trim(),
      href: normalizeAnchor(link?.link?.url || link?.anchorId || '', '#'),
    }))
    .filter(
      (item) => item.label && item.href && String(item.label).toLowerCase() !== 'footer',
    )
}

export function TopNavigationSection({ section }) {
  const pathname = usePathname()
  const sectionId = normalizeSectionId(section, 'top')
  const links = normalizeLinks(section?.navLinks)
  const primaryButton = section?.primaryButton
  const cta = primaryButton?.url && primaryButton?.title
    ? {
        href: normalizeAnchor(primaryButton.url, '#'),
        label: primaryButton.title,
        target: primaryButton.target || '',
      }
    : null

  function handleAnchorClick(event, href) {
    const targetId = getInPageAnchorId(href, pathname)

    if (!targetId) {
      return
    }

    event.preventDefault()

    const target = document.getElementById(targetId)
    if (!target) {
      return
    }

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return (
    <header id={sectionId} className="py-10">
      <Container className="relative">
        <nav className="relative z-50 flex justify-between">
          <div className="flex items-center md:gap-x-12">
            <Link href="#" aria-label="Home" className="inline-flex items-center">
              <Logo className="h-[1.875rem] w-auto" />
            </Link>
            <div className="hidden md:flex md:gap-x-6">
              {links.length
                ? links.map((link, index) => (
                    <NavLink
                      href={link.href}
                      key={`${link.label}-${index}`}
                      onAnchorClick={handleAnchorClick}
                    >
                      {link.label}
                    </NavLink>
                  ))
                : null}
            </div>
          </div>
          <div className="flex items-center gap-x-5 md:gap-x-8">
            {cta ? (
              <Button
                href={cta.href}
                color="blue"
                target={cta.target || undefined}
                rel={cta.target === '_blank' ? 'noreferrer' : undefined}
                className="!text-white hover:!text-white active:!text-white"
                style={{ color: 'var(--primary-foreground)' }}
              >
                {cta.label}
              </Button>
            ) : null}
            <div className="-mr-1 md:hidden">
              <MobileNavigation links={links} onAnchorClick={handleAnchorClick} />
            </div>
          </div>
        </nav>
      </Container>
    </header>
  )
}
